import {
  MAX_FLAT_FRAME_METERS,
  boundsExtentMeters,
  boundsOf,
  distanceMeters,
  formatLatLng,
  latLngToPixel,
  metersPerPixel,
  offsetLatLng,
  pixelToLatLng,
  zoomForExtent,
  type LatLng,
  type LatLngBounds,
} from './GeoMath';
import {
  applyMatrix3,
  isConvexQuad,
  quadToUnitSquare,
  unitSquareToQuad,
  type Point2,
} from './Homography';

/**
 * The selected site frame: a quadrilateral of the Earth, and everything downstream needs to know
 * about it.
 *
 * ## Why it is four corners rather than a centre and a size
 *
 * It began as a north-up square, which is exactly right for the plot that happens to sit square to
 * north and wrong for every other one. A house at 30° to the street framed by an axis-aligned square
 * either loses two of its corners or brings in two of the neighbour's, and the site model has no way
 * to express "the useful part is the diagonal bit".
 *
 * So the frame is four corners, in frame order — **top-left, top-right, bottom-right, bottom-left** —
 * and the three shapes anyone actually wants are all the same object:
 *
 * - **Square, north-up.** {@link createFrame}. What you get before touching anything.
 * - **Rotated rectangle.** {@link rotateFrame}. The common case, and *exact*: the projection stays a
 *   similarity, so no ground is stretched and the metre scale is uniform.
 * - **Free quadrilateral.** {@link moveCorner}. Drag any vertex. The frame is rectified to the site
 *   square on capture, which resamples the ground non-uniformly — a real cost, measured by
 *   {@link frameSkew} and stated in the readout and the prompt rather than hidden.
 *
 * ## Where the arithmetic lives
 *
 * One projective transform (`geo/Homography.ts`) built from the corners in Web Mercator pixel space,
 * mapping the quad onto the unit square. `frameToImage` *is* that transform; `frameToSite` is it
 * scaled to metres. The axis-aligned and rotated cases fall out of the same matrix with no special
 * casing and no accumulated drift, because the map is rebuilt from the corners rather than composed
 * from successive rotations.
 *
 * ## What the site gets
 *
 * The site model is a rectangular heightfield, and the rectified frame is that rectangle: `+X` is
 * frame-right, `+Z` is frame-down, spanning `±width/2` by `±length/2`. For an unrotated frame those
 * are still east and south. For a rotated one they are not, and {@link MapFrame.headingDegrees}
 * carries the difference into the prompt so the light can be read against true north.
 *
 * Pure data. `ImageryProviders` turns a frame into pixels; `SiteData` turns it into overlays.
 */

/** Site sizes the frame chooser offers, in metres. Chosen to bracket real residential plots. */
export const FRAME_SIZE_PRESETS: readonly number[] = [30, 45, 60, 90, 120, 180];

/** Largest and smallest ground extent a *site* frame may claim, matching the site model's limits. */
export const MIN_FRAME_METERS = 10;
export const MAX_FRAME_METERS = 200;

/**
 * Ceiling for a frame built only to be looked at.
 *
 * The browsing view is deliberately wider than the site quad inside it, and it is still a
 * `MapFrame` — so the clamp that keeps a *site* inside the terrain model's limits would otherwise
 * silently crop the context the view exists to provide.
 */
export const MAX_VIEW_METERS = MAX_FRAME_METERS * 3;

/**
 * Pixel edge of a captured frame — the image you actually get.
 *
 * Stated as the *final* count rather than the request parameter, because every provider spells the
 * request differently (`size=640&scale=2`, `640x640@2x`, `size=1280,1280`) and the only number the
 * rest of the app can reason about is how many pixels came back. {@link MapFrame.metersPerPixel} is
 * ground per one of these.
 */
export const FRAME_PIXELS = 1280;

/**
 * Pixel density the request is expressed in. `2` is what providers call `@2x` or `scale=2`, and it
 * is the difference between a legible fence line and a grey smear at residential zoom.
 */
export const FRAME_SCALE = 2;

/**
 * How far from rectangular a quad may drift before the UI calls it out.
 *
 * `0` is a perfect rectangle. The threshold is loose enough that a hand-dragged "close enough"
 * rectangle does not nag, and tight enough that a deliberate trapezoid does.
 */
export const SKEW_WARNING = 0.06;

/** Corners in frame order. Index 0 is the frame's top-left, and the winding is clockwise. */
export type FrameCorners = readonly [LatLng, LatLng, LatLng, LatLng];

/** Names for the corner handles, in {@link FrameCorners} order. */
export const CORNER_NAMES: readonly string[] = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];

export interface MapFrame {
  /** The four corners. The frame's shape is these and nothing else. */
  corners: FrameCorners;
  /** Centroid of the quad. Derived, kept for the readout and for re-centring. */
  center: LatLng;
  /** Integer tile zoom the imagery is cut at. */
  zoom: number;
  /** Pixels along each edge of the returned image. */
  pixels: number;
  /** Density the request is phrased in; `pixels / scale` is what a centre-zoom endpoint is asked for. */
  scale: number;
  /** Ground metres per returned image pixel, across the frame's width. */
  metersPerPixel: number;
  /** Mean of the top and bottom edges, in metres — the site's `X` extent. */
  widthMeters: number;
  /** Mean of the left and right edges, in metres — the site's `Z` extent. */
  lengthMeters: number;
  /**
   * How far the frame is turned clockwise from north-up, in degrees.
   *
   * `0` is north-up: image-up is north, image-right is east. At `30` the whole capture is rotated
   * 30° clockwise, so image-**up** points at bearing 30° and image-right at bearing 120°. Stated as
   * the turn rather than as a bearing because that is what the rotation handle produces and what the
   * readout shows; the two bearings the prompt needs are derived from it in one place.
   */
  headingDegrees: number;
  /** Which imagery this frame is cut from, for attribution and for re-capture. */
  providerId: string;
  /** ISO 8601. Imagery is dated; a site built from a 2019 capture should be able to say so. */
  capturedAt: string;
}

export interface FrameRequest {
  center: LatLng;
  /** Ground metres the frame should span. Snapped to what an integer zoom can actually deliver. */
  sizeMeters: number;
  providerId: string;
  /** Bearing of the frame's right edge, clockwise from north. */
  headingDegrees?: number;
  pixels?: number;
  scale?: number;
  now?: Date;
}

/**
 * Build a square frame around a point, optionally turned.
 *
 * The requested size is a *request*: tile imagery exists only at integer zooms, so the frame lands
 * on the nearest zoom that covers at least what was asked for, and reports the extent it actually
 * got. Returning the honest extent rather than the requested one is what keeps every downstream
 * metre true.
 */
export function createFrame(request: FrameRequest): MapFrame {
  const pixels = request.pixels ?? FRAME_PIXELS;
  const scale = request.scale ?? FRAME_SCALE;
  const size = clamp(request.sizeMeters, MIN_FRAME_METERS, MAX_VIEW_METERS);
  const zoom = zoomForExtent(request.center.lat, size, pixels, scale);
  // Ground per returned pixel, times the number of returned pixels. Writing it with the same
  // `pixels` on both sides is what keeps the extent honest.
  const mpp = metersPerPixel(request.center.lat, zoom, scale);
  const extent = mpp * pixels;
  const heading = normalizeHeading(request.headingDegrees ?? 0);

  return {
    corners: squareCorners(request.center, extent, heading),
    center: request.center,
    zoom,
    pixels,
    scale,
    metersPerPixel: mpp,
    widthMeters: extent,
    lengthMeters: extent,
    headingDegrees: heading,
    providerId: request.providerId,
    capturedAt: (request.now ?? new Date()).toISOString(),
  };
}

/** The four corners of a square of `extent` metres, centred and turned by `heading`. */
function squareCorners(center: LatLng, extent: number, heading: number): FrameCorners {
  const half = extent / 2;
  const radians = (heading * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  // Frame-local offsets (right, down) rotated into ground offsets (east, south). A clockwise
  // heading turns frame-right toward the south, which is the `-sin` in the south term.
  const corner = (right: number, down: number): LatLng =>
    offsetLatLng(center, right * cos - down * sin, right * sin + down * cos);

  return [corner(-half, -half), corner(half, -half), corner(half, half), corner(-half, half)];
}

/* -------------------------------- Reshaping -------------------------------- */

/** A frame turned to a new bearing, keeping its centre and its edge lengths. */
export function rotateFrame(frame: MapFrame, headingDegrees: number): MapFrame {
  const heading = normalizeHeading(headingDegrees);
  const delta = ((heading - frame.headingDegrees) * Math.PI) / 180;
  const cos = Math.cos(delta);
  const sin = Math.sin(delta);

  // Rotated about the centroid in ground metres, so a rotation never changes an edge length — which
  // is what makes turning the frame exact rather than a resample.
  const corners = frame.corners.map((point) => {
    const offset = groundOffset(frame.center, point);
    return offsetLatLng(
      frame.center,
      offset.east * cos - offset.south * sin,
      offset.east * sin + offset.south * cos,
    );
  }) as unknown as FrameCorners;

  return { ...frame, corners, headingDegrees: heading };
}

/**
 * A frame with one corner moved.
 *
 * Returns the frame unchanged when the drag would make the quad concave or self-crossing — the
 * algebra would happily solve such a quad and hand back a transform that folds the image over
 * itself, and that failure only becomes visible much later as a site with a mirrored corner.
 */
export function moveCorner(frame: MapFrame, index: number, to: LatLng): MapFrame {
  if (index < 0 || index > 3) return frame;
  const corners = [...frame.corners] as LatLng[];
  corners[index] = to;

  if (!isConvexQuad(corners.map((point) => latLngToPixel(point, frame.zoom)))) return frame;

  return withCorners(frame, corners as unknown as FrameCorners);
}

/** A frame at a new ground size, same centre and heading, square again. */
export function resizeFrame(frame: MapFrame, sizeMeters: number, now?: Date): MapFrame {
  return createFrame({
    center: frame.center,
    sizeMeters,
    providerId: frame.providerId,
    headingDegrees: frame.headingDegrees,
    pixels: frame.pixels,
    scale: frame.scale,
    ...(now ? { now } : {}),
  });
}

/** A frame moved bodily, corners and all. */
export function panFrame(frame: MapFrame, eastMeters: number, southMeters: number): MapFrame {
  const corners = frame.corners.map((point) =>
    offsetLatLng(point, eastMeters, southMeters),
  ) as unknown as FrameCorners;
  return {
    ...frame,
    corners,
    center: offsetLatLng(frame.center, eastMeters, southMeters),
  };
}

/**
 * The best rotated *rectangle* fitting the current quad — the "un-skew me" button.
 *
 * Takes the quad's centroid, its mean edge lengths and the bearing of its top edge, and rebuilds a
 * true rectangle from them. A frame dragged roughly into place then squared off this way keeps its
 * orientation and its size while losing the resampling cost a skewed quad carries.
 */
export function squareUpFrame(frame: MapFrame): MapFrame {
  const heading = edgeHeading(frame.corners[0], frame.corners[1]);
  const width = frame.widthMeters;
  const length = frame.lengthMeters;
  const radians = (heading * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const corner = (right: number, down: number): LatLng =>
    offsetLatLng(frame.center, right * cos - down * sin, right * sin + down * cos);

  const corners: FrameCorners = [
    corner(-width / 2, -length / 2),
    corner(width / 2, -length / 2),
    corner(width / 2, length / 2),
    corner(-width / 2, length / 2),
  ];
  return withCorners({ ...frame, headingDegrees: heading }, corners);
}

/** Recompute everything a frame derives from its corners. */
function withCorners(frame: MapFrame, corners: FrameCorners): MapFrame {
  const center = centroid(corners);
  const top = distanceMeters(corners[0], corners[1]);
  const bottom = distanceMeters(corners[3], corners[2]);
  const left = distanceMeters(corners[0], corners[3]);
  const right = distanceMeters(corners[1], corners[2]);

  // Opposite edges are averaged rather than maxed: the rectified square is the *mean* rectangle, so
  // averaging is the extent that minimises how far any edge is stretched to reach it.
  const widthMeters = (top + bottom) / 2;
  const lengthMeters = (left + right) / 2;

  // Re-derived, not carried over. A frame dragged to twice its size still has to be *fetched*, and a
  // centre/zoom endpoint is asked for `frame.zoom` — so a stale zoom here comes back later as a
  // capture at the wrong scale, on the one provider whose URL cannot recover the size from a bbox.
  const zoom = zoomForExtent(center.lat, widthMeters, frame.pixels, frame.scale);

  return {
    ...frame,
    corners,
    center,
    zoom,
    widthMeters,
    lengthMeters,
    metersPerPixel: widthMeters / Math.max(1, frame.pixels),
    headingDegrees: edgeHeading(corners[0], corners[1]),
  };
}

/**
 * How far from a rectangle a frame has drifted, as a fraction.
 *
 * Zero for any rectangle at any rotation. Grows with the mismatch between opposite edges and with
 * corners that are not square. Used to decide whether the UI should mention that capturing will
 * resample the ground.
 */
export function frameSkew(frame: MapFrame): number {
  const [a, b, c, d] = frame.corners;
  const top = distanceMeters(a, b);
  const bottom = distanceMeters(d, c);
  const left = distanceMeters(a, d);
  const right = distanceMeters(b, c);
  const opposite =
    Math.abs(top - bottom) / Math.max(1e-6, top + bottom) +
    Math.abs(left - right) / Math.max(1e-6, left + right);

  // Diagonals are equal in a rectangle and diverge as the corners stop being square — which catches
  // a parallelogram, whose opposite edges match perfectly.
  const diagonalA = distanceMeters(a, c);
  const diagonalB = distanceMeters(b, d);
  const diagonal = Math.abs(diagonalA - diagonalB) / Math.max(1e-6, diagonalA + diagonalB);

  return opposite + diagonal * 2;
}

/** Whether capturing this frame will stretch the ground rather than merely turn it. */
export function frameIsRectangular(frame: MapFrame): boolean {
  return frameSkew(frame) <= SKEW_WARNING;
}

/* -------------------------------- Projection -------------------------------- */

/**
 * The frame's own pixel space: corner positions relative to the frame's centre.
 *
 * The relative part is load-bearing. Global Web Mercator pixels at zoom 19 run to ~1.3 × 10⁸, and
 * the homography's 8×8 system multiplies pairs of them — which spends roughly eight of a double's
 * sixteen digits before the solve begins, and leaves a frame's own corner landing at `1.000001`
 * instead of `1`. Subtracting the centre first drops the magnitudes to a few hundred and the error
 * to the twelfth decimal. It costs one subtraction and it is the difference between a projection
 * that closes and one that nearly does.
 *
 * Rebuilt on demand rather than cached on the frame: a `MapFrame` is passed around, serialised and
 * spread, and a cached matrix on it would be one `{...frame, corners}` away from describing the
 * previous shape.
 */
function frameSpace(frame: MapFrame): {
  origin: Point2;
  corners: readonly [Point2, Point2, Point2, Point2];
} {
  const pixels = frame.corners.map((point) => latLngToPixel(point, frame.zoom));
  const origin = {
    x: pixels.reduce((sum, p) => sum + p.x, 0) / pixels.length,
    y: pixels.reduce((sum, p) => sum + p.y, 0) / pixels.length,
  };
  const corners = pixels.map((p) => ({ x: p.x - origin.x, y: p.y - origin.y }));
  return { origin, corners: corners as unknown as readonly [Point2, Point2, Point2, Point2] };
}

/**
 * A point on Earth in the frame's normalized image space — the `[u, v]` stage 1 and the reference
 * tags both speak.
 *
 * Coordinates outside `[0, 1]` mean outside the frame, and are returned rather than clamped: a
 * parcel corner that spills past the edge is a finding, not a rendering artifact.
 */
export function frameToImage(frame: MapFrame, point: LatLng): [number, number] {
  const { origin, corners } = frameSpace(frame);
  const matrix = quadToUnitSquare(corners);
  if (!matrix) return [0.5, 0.5];
  const pixel = latLngToPixel(point, frame.zoom);
  const mapped = applyMatrix3(matrix, { x: pixel.x - origin.x, y: pixel.y - origin.y });
  return mapped ? [mapped.x, mapped.y] : [Number.NaN, Number.NaN];
}

/** A point on Earth in the frame's site metres — the pipeline's world space. */
export function frameToSite(frame: MapFrame, point: LatLng): { x: number; z: number } {
  const [u, v] = frameToImage(frame, point);
  return { x: (u - 0.5) * frame.widthMeters, z: (v - 0.5) * frame.lengthMeters };
}

/** The inverse: a point in site metres back to Earth. */
export function siteToFrame(frame: MapFrame, x: number, z: number): LatLng {
  return imageToFrame(
    frame,
    x / Math.max(1e-6, frame.widthMeters) + 0.5,
    z / Math.max(1e-6, frame.lengthMeters) + 0.5,
  );
}

/** A point in normalized image space back to Earth — what a corner drag needs. */
export function imageToFrame(frame: MapFrame, u: number, v: number): LatLng {
  const { origin, corners } = frameSpace(frame);
  const matrix = unitSquareToQuad(corners);
  if (!matrix) return frame.center;
  const mapped = applyMatrix3(matrix, { x: u, y: v });
  return mapped ? pixelToLatLng(mapped.x + origin.x, mapped.y + origin.y, frame.zoom) : frame.center;
}

/** Whether a point falls inside the quad. */
export function frameContains(frame: MapFrame, point: LatLng): boolean {
  const [u, v] = frameToImage(frame, point);
  return Number.isFinite(u) && Number.isFinite(v) && u >= 0 && u <= 1 && v >= 0 && v <= 1;
}

/** The frame's geographic bounds — the axis-aligned rectangle imagery must be fetched for. */
export function frameBounds(frame: MapFrame): LatLngBounds {
  return boundsOf(frame.corners) as LatLngBounds;
}

/**
 * Whether treating this frame as a plane is still honest.
 *
 * Never false for any site this tool can model; it exists so that a caller reaching for a
 * city-scale frame trips over the assumption in `GeoMath` rather than inheriting it.
 */
export function frameIsFlatEnough(frame: MapFrame): boolean {
  return frame.widthMeters <= MAX_FLAT_FRAME_METERS;
}

/** A frame that covers a boundary, with a margin — how "fit to the parcel" is expressed. */
export function frameForBounds(
  bounds: LatLngBounds,
  providerId: string,
  marginFraction = 0.15,
  now?: Date,
): MapFrame {
  const extent = boundsExtentMeters(bounds);
  const size = Math.max(extent.widthMeters, extent.lengthMeters) * (1 + marginFraction * 2);
  return createFrame({
    center: { lat: (bounds.north + bounds.south) / 2, lng: (bounds.east + bounds.west) / 2 },
    sizeMeters: size,
    providerId,
    ...(now ? { now } : {}),
  });
}

/* --------------------------------- Reporting --------------------------------- */

/** One line for the UI: where it is, how big it is, which way it faces, how sharp it is. */
export function describeFrame(frame: MapFrame): string {
  const turn = frame.headingDegrees === 0 ? 'north-up' : `${Math.round(frame.headingDegrees)}° turn`;
  return (
    `${formatLatLng(frame.center, 5)}  ·  ` +
    `${Math.round(frame.widthMeters)} × ${Math.round(frame.lengthMeters)} m  ·  ${turn}  ·  ` +
    `${frame.metersPerPixel.toFixed(2)} m/px at z${frame.zoom}`
  );
}

/** The four edge lengths, for a readout that has to show a quad is not a rectangle. */
export function frameEdgeMeters(frame: MapFrame): {
  top: number;
  right: number;
  bottom: number;
  left: number;
} {
  const [a, b, c, d] = frame.corners;
  return {
    top: distanceMeters(a, b),
    right: distanceMeters(b, c),
    bottom: distanceMeters(c, d),
    left: distanceMeters(d, a),
  };
}

/**
 * The frame as a prompt fragment.
 *
 * Written as an instruction to *stop estimating*, not as a hint. Stage 1's scale procedure is the
 * most expensive guess in the pipeline, and a model handed both a known extent and a method for
 * deriving one will often produce the second and quietly contradict the first.
 *
 * Two things a shaped frame has to add. **Heading**: once the frame is turned, image-up is no longer
 * north, and a stage reading shadows without knowing that will place the sun 30° out and pick a time
 * of day to match — so the bearing is stated and the stage is told to read the light against it.
 * **Rectification**: a skewed quad has been stretched to fill the square, so the ground scale is no
 * longer uniform, and saying so is the difference between an approximation and a lie.
 */
export function frameBrief(frame: MapFrame): string {
  const size = frame.widthMeters.toFixed(1);
  const length = frame.lengthMeters.toFixed(1);
  const edges = frameEdgeMeters(frame);
  const rectangular = frameIsRectangular(frame);

  const turn = Math.round(frame.headingDegrees);
  const orientation =
    turn === 0
      ? `- Orientation: north is up. Image-right is east, image-down is south.`
      : `- Orientation: the capture is TURNED ${turn}° clockwise from north-up. Image-UP points at
  bearing ${turn}° and image-RIGHT at bearing ${Math.round(normalizeHeading(turn + 90))}°, both
  clockwise from true north. Read shadow directions against those bearings, not against the image
  edges — otherwise the sun lands ${turn}° out and you will choose a time of day to match the error.`;

  const rectification = rectangular
    ? `The capture is a true rectangle on the ground: the metre scale is uniform everywhere in it.`
    : `The frame was dragged into a quadrilateral and RECTIFIED to fill this square, so the metre
scale is NOT perfectly uniform across the image. The edges measured, before rectification:
top ${edges.top.toFixed(1)} m, right ${edges.right.toFixed(1)} m, bottom ${edges.bottom.toFixed(1)} m,
left ${edges.left.toFixed(1)} m. Use the site extent below as the authority; treat lengths you
measure near the more-stretched edges as approximate, and say so in "notes".`;

  return `# The site extent is KNOWN — do not estimate it

This photograph is not a camera frame. It is an orthographic map capture with a known projection, so
its scale is measured rather than inferred:

- Centre: ${formatLatLng(frame.center, 6)}
- Ground extent: ${size} m across by ${length} m down, edge to edge
- Resolution: ${frame.metersPerPixel.toFixed(3)} metres per image pixel (Web Mercator, zoom ${frame.zoom})
${orientation}

Use exactly these numbers for "site.widthMeters" and "site.lengthMeters", and report the scale as
measured from the capture rather than from an object in frame. Do NOT search for a car or a door to
scale against, and do NOT adjust the extent because the plot looks larger or smaller than it: the
frame is the site, and anything spilling past its edge is outside the site by definition.

${rectification}

Because the view is orthographic there is no perspective to correct for. A region covering a third
of the frame covers a third of the site.

What the capture cannot tell you, and you must still read from the pixels: the light and the shadow
direction, the season, and every region boundary.`;
}

/* ---------------------------------- Helpers ---------------------------------- */

/** Ground offset from `origin` to `point`, in metres east and south. */
function groundOffset(origin: LatLng, point: LatLng): { east: number; south: number } {
  const latPerMeter = 180 / (Math.PI * 6378137);
  return {
    east: (point.lng - origin.lng) * Math.cos((origin.lat * Math.PI) / 180) / latPerMeter,
    south: (origin.lat - point.lat) / latPerMeter,
  };
}

/** Bearing from one corner to the next, clockwise from north. */
function edgeHeading(from: LatLng, to: LatLng): number {
  const offset = groundOffset(from, to);
  if (Math.abs(offset.east) < 1e-9 && Math.abs(offset.south) < 1e-9) return 0;
  return normalizeHeading((Math.atan2(offset.south, offset.east) * 180) / Math.PI);
}

function centroid(corners: FrameCorners): LatLng {
  const lat = corners.reduce((sum, point) => sum + point.lat, 0) / corners.length;
  const lng = corners.reduce((sum, point) => sum + point.lng, 0) / corners.length;
  return { lat, lng };
}

export function normalizeHeading(degrees: number): number {
  const wrapped = ((degrees % 360) + 360) % 360;
  // -0 reads as "0" everywhere except a strict equality check, which the tests do use.
  return wrapped === 0 ? 0 : wrapped;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
